import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BoxReturnIcon, DollarSignIcon, WarrantyIcon } from "@/components/icons/assets";

interface WhyChooseProps {
  showBadge?: boolean;
  className?: string;
  variant?: "home" | "about";
}

export default function WhyChooseSection({
  showBadge = true,
  className = "",
  variant = "home",
}: WhyChooseProps) {
  const isAbout = variant === "about";
  const sectionBg = isAbout ? "bg-background-50" : "";
  const cardBg = isAbout ? "bg-white" : "bg-background-50";

  return (
    <section className={`px-4 md:px-8 lg:px-20 pt-30 pb-20 ${sectionBg} ${className}`}>
      <>
        {/* Header */}
        <div className="text-center mb-12">
          {showBadge && (
            <Badge variant="outline" className="font-plus-jakarta font-semibold px-6 py-2 bg-background-100 border-stroke text-neutral-500 rounded-full text-base mb-5">
              Why Choose Peoples WheelTox
            </Badge>
          )}
          <h2 className="font-plus-jakarta text-4xl md:text-5xl font-bold text-neutral-900 leading-tight mb-5 max-w-xl mx-auto">
            WHY DRIVERS CHOOSE
            WHEELTOX
          </h2>
          <p className="font-plus-jakarta text-sm text-neutral-300 max-w-2xl mx-auto">
            WheelTox is built around trust, quality, and transparency. From fair
            pricing to secure payments and reliable delivery, we focus on giving
            drivers a better way to buy and sell wheels.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Professional Delivery Card */}
          <Card className={`${cardBg} row-span-2 border-none shadow-none max-w-[652px] h-[700px] rounded-xl`}>
            <CardContent className="p-[40px] flex flex-col justify-between h-full">
              <div>
                <h3 className="font-plus-jakarta text-xl font-bold text-neutral-900 mb-2">
                  PROFESSIONAL DELIVERY
                </h3>
                <p className="text-neutral-300 font-regular font-plus-jakarta">
                  We offer fast and reliable delivery to major Australian
                  cities, ensuring your commercial equipment arrives safely and
                  on time.
                </p>
              </div>

              {/* Delivery Icon */}
              <div className="flex justify-center mt-8">
                <BoxReturnIcon />
              </div>
            </CardContent>
          </Card>

          {/* Fair & Transparent Pricing Card */}
          <Card className={`${cardBg} border-none shadow-none rounded-xl`}>
            <CardContent className="p-8 h-full flex flex-col justify-between">
              {/* Dollar Icon */}
              <div className="flex justify-center mb-6">
                <DollarSignIcon />
              </div>
              <div>
                <h3 className="font-plus-jakarta text-xl font-bold text-neutral-900 mb-2">
                  FAIR & TRANSPARENT PRICING
                </h3>
                <p className="text-neutral-300 font-regular font-plus-jakarta">
                  Clear pricing with no hidden fees — buy and sell at fair market
                  value.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Warranty & Support Card */}
          <Card className={`${cardBg} border-none shadow-none rounded-xl`}>
            <CardContent className="p-8 h-full flex flex-col justify-between">
              {/* Warranty Badge */}
              <div className="flex justify-center mb-6">
                <WarrantyIcon />
              </div>
              <div>
                <h3 className="font-plus-jakarta text-xl font-bold text-neutral-900 mb-2">
                  WARRANTY & SUPPORT
                </h3>
                <p className="text-neutral-300 font-regular font-plus-jakarta">
                  All new equipment comes with a 12-36 month manufacturer&apos;s
                  warranty, giving you confidence and peace of mind.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    </section>
  );
}
